import React, { useCallback } from 'react'

import { useNavigate } from 'react-router'
import { useSelectedTrial } from 'src/features/auth/context'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'
import { assertNever } from 'src/utils/assertNever'

import { CreateTemplateModal, SubmitValues } from './create-template-modal'
import {
  create,
  importTemplate,
} from '../../../../../entities/template/request'
import { useRequestState } from '../../../../../server/use-request-state'
import { getDetailRoute } from '../routes'
import { useTemplateEntities } from '../template-entities-context'

const useSubmit = (templateFolderUid: string) => {
  const { selectedTrial } = useSelectedTrial()
  const trialUid = selectedTrial.uid

  const {
    requesting,
    errorMessage,
    requestDone,
    requestFailed,
    requestStarted,
  } = useRequestState()
  const { upsertTemplate } = useTemplateEntities()
  const navigate = useNavigate()
  const toast = useMirohaToast()
  const request = useCallback(
    async ({
      type,
      title,
      fileName,
      content,
      presetTemplate,
    }: SubmitValues) => {
      try {
        requestStarted()

        switch (type) {
          case 'New': {
            const newTemplate = await create({
              trialUid,
              title,
              templateFolderUid,
              presetTemplateUid: presetTemplate?.uid,
            })

            upsertTemplate(newTemplate)
            navigate(getDetailRoute(newTemplate))

            break
          }
          case 'Import': {
            const importedTemplate = await importTemplate({
              trialUid,
              title,
              fileName,
              content,
              templateFolderUid,
            })

            upsertTemplate(importedTemplate)
            navigate(getDetailRoute(importedTemplate))

            break
          }
          case 'Preset': {
            if (!presetTemplate) return

            const presetUsedTemplate = await create({
              trialUid,
              title,
              templateFolderUid,
              presetTemplateUid: presetTemplate.uid,
              videoEnabled: presetTemplate.videoEnabled,
            })

            upsertTemplate(presetUsedTemplate)
            navigate(getDetailRoute(presetUsedTemplate))

            break
          }
          default:
            return assertNever(type)
        }

        requestDone()
        toast({ status: 'success', title: 'テンプレートを作成しました。' })
      } catch (error) {
        requestFailed(error.message)
        throw error
      }
    },
    [
      requestStarted,
      requestDone,
      toast,
      trialUid,
      templateFolderUid,
      upsertTemplate,
      navigate,
      requestFailed,
    ],
  )

  return { request, requesting, errorMessage }
}

type Props = {
  templateFolderUid: string

  onClose: () => void
}

export const CreateTemplateModalContainer: React.FC<Props> = props => {
  const { templateFolderUid } = props

  const { getTemplateFolder, presetFolder, getTemplatesByFolderUid } =
    useTemplateEntities()
  const templateFolder = getTemplateFolder(templateFolderUid)
  const presetTemplates = getTemplatesByFolderUid(
    presetFolder ? presetFolder.uid : '',
  )

  const { request, requesting, errorMessage } = useSubmit(templateFolderUid)
  const onSubmit = useCallback(
    async (params: SubmitValues) => {
      await request(params)
      props.onClose()
    },
    [props, request],
  )

  if (!templateFolder) return null

  return (
    <CreateTemplateModal
      templateFolderName={templateFolder.name}
      presetTemplates={presetTemplates}
      requesting={requesting}
      errorMessage={errorMessage}
      onClose={props.onClose}
      onSubmit={onSubmit}
    />
  )
}
